import React from 'react';
import { ReportSection, ReportSubsection } from '../framework';
import { CalloutBox, ComparisonGrid, TwoColumnComparison } from '../primitives';
import { SectionProps } from './types';

export const SituationSection: React.FC<Pick<SectionProps, 'assumptions' | 'formatCurrency'>> = ({
  assumptions,
  formatCurrency
}) => {
  // Calculate property tax amounts
  const currentPropTax = Math.round(assumptions.currentAssessedValue * assumptions.propertyTaxRate / 100);
  const reassessedPropTax = Math.round(assumptions.propertyValue * assumptions.propertyTaxRate / 100);
  const historicalAppreciation = assumptions.propertyValue - assumptions.originalPurchasePrice;
  const taxIncrease = reassessedPropTax - currentPropTax;

  return (
    <ReportSection id="situation" title="Your Specific Situation" subtitle="The inheritance, taxes, and ownership structure">
      <p className="text-gray-700 mb-6">
        Before we dive into general principles about real estate vs. stocks, there are three California-specific
        factors that fundamentally shape this decision. These aren't footnotes—they're the most important
        things to understand.
      </p>

      <ReportSubsection title="The Step-Up in Basis">
        <div className="bg-gray-50 border border-gray-200 p-4 mb-4">
          <p className="font-semibold text-gray-900 mb-2">Key Point: Historical Appreciation Is Tax-Free</p>
          <p className="text-gray-700 text-sm">
            Because this property is held in a revocable living trust and will transfer at death, you receive
            a <strong>"stepped-up" cost basis</strong> to the fair market value at the date of death. Your mother's
            original purchase price (~{formatCurrency(assumptions.originalPurchasePrice)}) becomes irrelevant—you won't owe taxes on that ~{formatCurrency(historicalAppreciation)} of historical
            appreciation regardless of when you sell.
          </p>
        </div>

        <p className="text-gray-700 mb-4">
          Here's what this means in practice:
        </p>

        <TwoColumnComparison
          left={{
            title: 'If You Sell Soon',
            content: (
              <>
                Sale price ≈ stepped-up basis, so minimal taxable gain.
                <span className="block mt-2 font-medium text-gray-900">Capital gains tax: ~$0</span>
              </>
            )
          }}
          right={{
            title: 'If You Hold and Sell in 10 Years',
            content: (
              <>
                Only the appreciation <em>after</em> inheritance is taxable—not the historical gains.
                <span className="block mt-2 font-medium text-gray-900">Capital gains tax: ~20-37% on post-inheritance gains only (varies by income)</span>
              </>
            )
          }}
        />

        <CalloutBox type="insight" title="The Flexibility Angle">
          <p>
            The step-up doesn't create a "window" that closes—you'll never owe tax on the historical gains.
            But there's a subtler advantage to selling now: <strong>tax flexibility</strong>. With ~{formatCurrency(assumptions.propertyValue)} in stocks,
            you can sell portions gradually, harvest losses to offset gains, and optimize your tax situation year
            by year. With one ~{formatCurrency(assumptions.propertyValue)} house, it's all-or-nothing—when you eventually sell, you realize all the gains
            at once in a single tax year.
          </p>
        </CalloutBox>
      </ReportSubsection>

      <ReportSubsection title="Prop 19: The Property Tax Question">
        <div className="bg-gray-50 border border-gray-200 p-4 mb-4">
          <p className="font-semibold text-gray-900 mb-2">Property Tax Will Likely Increase Substantially</p>
          <p className="text-gray-700 text-sm">
            California's Proposition 19 (passed in 2020) changed the rules for inherited properties. To preserve
            the low Prop 13 assessed value, an eligible heir (child or grandchild meeting specific criteria) must
            move in as their <strong>primary residence within one year</strong> and file a homeowner's exemption.
            For properties used as rentals, reassessment to market value is generally required.
          </p>
        </div>

        <p className="text-gray-700 mb-4">
          Currently, this property has a Prop 13-protected assessed value of approximately {formatCurrency(assumptions.currentAssessedValue)}, with annual
          property taxes around {formatCurrency(currentPropTax)}. If reassessed to market value (~{formatCurrency(assumptions.propertyValue)}), taxes would increase to ~{formatCurrency(reassessedPropTax)}/year.
        </p>

        <ComparisonGrid
          items={[
            { label: 'Current (Prop 13 Protected)', value: `~${formatCurrency(currentPropTax)}/year`, sublabel: `Based on ${formatCurrency(assumptions.currentAssessedValue)} assessed value` },
            { label: 'If Reassessed to Market Value', value: `~${formatCurrency(reassessedPropTax)}/year`, sublabel: `Based on ~${formatCurrency(assumptions.propertyValue)} market value` }
          ]}
        />

        <p className="text-gray-700 mt-4 mb-4">
          This analysis assumes the property will be reassessed (since no heir is moving in). The ~{formatCurrency(taxIncrease)} annual
          increase is baked into the rental cash flow calculations.
        </p>

        <p className="text-gray-700">
          <strong>Important:</strong> After reassessment, Prop 13's 2% annual cap on assessed value growth kicks in again.
          If property appreciates faster than 2% nominal (~{assumptions.propertyAppreciation}% real + ~{assumptions.inflationRate}% inflation = ~{(assumptions.propertyAppreciation + assumptions.inflationRate).toFixed(1)}% nominal),
          taxes will grow slower than property value over time. This analysis models this correctly—property taxes
          grow at the Prop 13 cap (2%), not at the rate of market appreciation.
        </p>
      </ReportSubsection>
    </ReportSection>
  );
};
